// Global Instructions Rule Applied!
//
// Configuration → Deal Execution (signed-off Mockup 9, Phase 1 scope):
//   1. Deal type → execution pipeline mapping (which pipeline a deal enters).
//   2. Per-stage checklist items for the execution pipelines (add / rename /
//      resequence / activate / soft-delete).
// Pipelines and stages themselves are managed in Configuration → Pipelines
// (execution pipelines are ordinary pipelines of type Deal Execution).
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Card, Input, InputNumber, Popconfirm, Select, Switch, Table } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { PIPELINE_TYPE_DEAL_EXECUTION } from '../../../state_manager/globals.ts'
import { PipelinesModel } from '../../../state_manager/data_models/pipelines_model.ts'
import { PipelineConfigModel } from '../../../state_manager/data_models/pipeline_config_model.ts'
import { PipelineStagesModel } from '../../../state_manager/data_models/pipeline_stages_model.ts'
import {
  DealExecutionMappingRow,
  StageChecklistAdminRow
} from '../../../state_manager/data_models/deal_execution_admin_model.ts'
import {
  createStageChecklistItem,
  deleteStageChecklistItem,
  fetchDealExecutionMapping,
  fetchStageChecklistItems,
  setDealExecutionMapping,
  updateStageChecklistItem
} from '../services/dealExecutionConfigService.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

export default function DealExecutionPanel(): React.ReactElement {
  const token = () => getAccessToken(store.getState().auth)

  const [mapping, setMapping] = useState<DealExecutionMappingRow[]>([])
  const [mappingLoading, setMappingLoading] = useState(false)
  const [items, setItems] = useState<StageChecklistAdminRow[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [selectedPipeline, setSelectedPipeline] = useState<string | null>(null)
  const [selectedConfig, setSelectedConfig] = useState<string | null>(null)
  const [newLabel, setNewLabel] = useState('')

  const pipelines = useSelector(
    (state: { globalConfigs?: { pipelines?: PipelinesModel[] } }) => state.globalConfigs?.pipelines || []
  )
  const pipelineConfigs = useSelector(
    (state: { globalConfigs?: { pipeline_configs?: PipelineConfigModel[] } }) =>
      state.globalConfigs?.pipeline_configs || []
  )
  const pipelineStages = useSelector(
    (state: { globalConfigs?: { pipeline_stages?: PipelineStagesModel[] } }) =>
      state.globalConfigs?.pipeline_stages || []
  )

  const executionPipelines = useMemo(
    () => pipelines.filter((p) => p.type === PIPELINE_TYPE_DEAL_EXECUTION),
    [pipelines]
  )

  const stageOptions = useMemo(() => {
    if (!selectedPipeline) return []
    return pipelineConfigs
      .filter((cfg) => cfg.pipeline === selectedPipeline)
      .map((cfg) => {
        const stage = pipelineStages.find((s) => s.id === cfg.stage)
        return {
          value: cfg.id as string,
          label: (stage?.name as string) || 'Stage',
          sequence: (cfg.sequence_kb as number | null) ?? 0
        }
      })
      .sort((a, b) => a.sequence - b.sequence)
  }, [selectedPipeline, pipelineConfigs, pipelineStages])

  const loadMapping = useCallback(async () => {
    setMappingLoading(true)
    try {
      setMapping(await fetchDealExecutionMapping(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load mapping')
    } finally {
      setMappingLoading(false)
    }
  }, [])

  const loadItems = useCallback(async () => {
    setItemsLoading(true)
    try {
      setItems(await fetchStageChecklistItems(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load checklist items')
    } finally {
      setItemsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadMapping()
    loadItems()
  }, [loadMapping, loadItems])

  const handleMappingChange = async (dealTypeId: string, pipelineId: string | null): Promise<void> => {
    try {
      await setDealExecutionMapping(token(), dealTypeId, pipelineId)
      toast.success('Mapping updated — takes effect for newly entering deals')
      await loadMapping()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to update mapping')
    }
  }

  const stageItems = useMemo(
    () =>
      items
        .filter((i) => i.pipeline_config_id === selectedConfig)
        .sort((a, b) => (a.stage_checklist_items__sequence ?? 0) - (b.stage_checklist_items__sequence ?? 0)),
    [items, selectedConfig]
  )

  const handleAddItem = async (): Promise<void> => {
    if (!selectedConfig || !newLabel.trim()) return
    try {
      const nextSeq = Math.max(0, ...stageItems.map((i) => i.stage_checklist_items__sequence ?? 0)) + 1
      await createStageChecklistItem(token(), {
        pipeline_config_id: selectedConfig,
        label: newLabel.trim(),
        sequence: nextSeq
      })
      setNewLabel('')
      await loadItems()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to add item')
    }
  }

  const handleItemUpdate = async (
    itemId: string,
    fields: { label?: string; sequence?: number; active?: boolean }
  ): Promise<void> => {
    try {
      await updateStageChecklistItem(token(), itemId, fields)
      await loadItems()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to update item')
    }
  }

  const handleItemDelete = async (itemId: string): Promise<void> => {
    try {
      await deleteStageChecklistItem(token(), itemId)
      await loadItems()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to delete item')
    }
  }

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      <Card
        size='small'
        title='Deal type → execution pipeline'
        extra={
          <span className='text-xs text-gray-400'>
            Which pipeline a deal enters (on Committed, or immediately for portfolio companies)
          </span>
        }
      >
        <Table
          size='small'
          rowKey='deal_type_id'
          loading={mappingLoading}
          pagination={false}
          dataSource={mapping}
          columns={[
            { title: 'Deal type', dataIndex: 'deal_type__label', render: (v) => v || '—' },
            {
              title: 'Execution pipeline',
              key: 'pipeline',
              render: (_, row) => (
                <Select
                  allowClear
                  placeholder='Not mapped — deals of this type never auto-enter'
                  className='min-w-[280px]'
                  value={row.pipeline_id ?? undefined}
                  onChange={(value) => handleMappingChange(row.deal_type_id, value ?? null)}
                  options={executionPipelines.map((p) => ({ value: p.id, label: p.name }))}
                />
              )
            }
          ]}
        />
      </Card>

      <Card
        size='small'
        title='Stage checklists'
        extra={
          <span className='text-xs text-gray-400'>
            Items attach to a pipeline stage; numbering across the pipeline is derived automatically
          </span>
        }
      >
        <div className='flex gap-3 mb-3'>
          <Select
            placeholder='Pipeline'
            className='min-w-[260px]'
            value={selectedPipeline ?? undefined}
            onChange={(value) => {
              setSelectedPipeline(value)
              setSelectedConfig(null)
            }}
            options={executionPipelines.map((p) => ({ value: p.id, label: p.name }))}
          />
          <Select
            placeholder='Stage'
            className='min-w-[220px]'
            value={selectedConfig ?? undefined}
            onChange={(value) => setSelectedConfig(value)}
            options={stageOptions}
            disabled={!selectedPipeline}
          />
        </div>

        {selectedConfig ? (
          <>
            <Table
              size='small'
              rowKey='stage_checklist_items__id'
              loading={itemsLoading}
              pagination={false}
              dataSource={stageItems}
              columns={[
                {
                  title: '#',
                  dataIndex: 'stage_checklist_items__sequence',
                  width: 90,
                  render: (v, row) => (
                    <InputNumber
                      size='small'
                      min={0}
                      value={v ?? 0}
                      onBlur={(e) => {
                        const next = Number((e.target as HTMLInputElement).value)
                        if (!Number.isNaN(next) && next !== v) {
                          handleItemUpdate(row.stage_checklist_items__id, { sequence: next })
                        }
                      }}
                    />
                  )
                },
                {
                  title: 'Item',
                  dataIndex: 'stage_checklist_items__label',
                  render: (v, row) => (
                    <Input
                      size='small'
                      defaultValue={v || ''}
                      onBlur={(e) => {
                        const next = e.target.value.trim()
                        if (next && next !== v) {
                          handleItemUpdate(row.stage_checklist_items__id, { label: next })
                        }
                      }}
                    />
                  )
                },
                {
                  title: 'Active',
                  dataIndex: 'stage_checklist_items__active',
                  width: 80,
                  render: (v, row) => (
                    <Switch
                      size='small'
                      checked={v !== false}
                      onChange={(checked) =>
                        handleItemUpdate(row.stage_checklist_items__id, { active: checked })
                      }
                    />
                  )
                },
                {
                  title: '',
                  key: 'actions',
                  width: 50,
                  render: (_, row) => (
                    <Popconfirm
                      title='Delete this checklist item?'
                      description='Recorded answers keep their audit trail.'
                      onConfirm={() => handleItemDelete(row.stage_checklist_items__id)}
                    >
                      <Button size='small' type='text' danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )
                }
              ]}
            />
            <div className='flex gap-2 mt-3'>
              <Input
                placeholder='New checklist item…'
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onPressEnter={handleAddItem}
                className='max-w-[420px]'
              />
              <Button type='primary' icon={<PlusOutlined />} onClick={handleAddItem} disabled={!newLabel.trim()}>
                Add item
              </Button>
            </div>
          </>
        ) : (
          <div className='text-sm text-gray-400 py-6 text-center'>
            Pick a pipeline and stage to manage its checklist.
          </div>
        )}
      </Card>
    </div>
  )
}
